import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu as MenuIcon, X, Phone } from "lucide-react";
import { BRAND } from "../../data/menu";

const LINKS = [
  { id: "order", label: "Order Online" },
  { id: "story", label: "Our Story" },
  { id: "menu", label: "The Menu" },
  { id: "specials", label: "Specials" },
  { id: "visit", label: "Visit" },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id) => {
    setOpen(false);
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <motion.header
      data-testid="site-nav"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed top-0 left-0 right-0 z-[100] transition-[background-color,box-shadow,padding] duration-500 ${
        scrolled
          ? "bg-[#f7f0e0]/95 backdrop-blur-md py-3 shadow-[0_8px_30px_rgba(122,31,31,0.08)]"
          : "bg-transparent py-5"
      }`}
    >
      <div className="mx-auto max-w-7xl px-5 md:px-8 flex items-center justify-between">
        <button
          data-testid="nav-logo"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex items-center gap-3"
        >
          <img src={BRAND.logo} alt="Puneri Kitchen" className="h-11 w-11 rounded-full object-cover ring-1 ring-[#7a1f1f]/20" />
          <span className="hidden sm:flex flex-col leading-none text-left">
            <span className="font-display text-xl font-bold text-[#7a1f1f] tracking-tight">Puneri Kitchen</span>
            <span className="text-[10px] uppercase tracking-[0.28em] text-[#5c3a3a]">Since Ghar · Pune</span>
          </span>
        </button>

        <nav className="hidden md:flex items-center gap-9">
          {LINKS.map((l) => (
            <button
              key={l.id}
              data-testid={`nav-link-${l.id}`}
              onClick={() => go(l.id)}
              className="relative text-sm font-medium text-[#331818] group"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-[2px] w-0 bg-[#d97706] transition-[width] duration-300 group-hover:w-full" />
            </button>
          ))}
          <a
            data-testid="nav-call-btn"
            href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
            className="inline-flex items-center gap-2 rounded-full bg-[#7a1f1f] px-5 py-2.5 text-sm font-semibold text-[#f7f0e0] transition-colors duration-300 hover:bg-[#d97706]"
          >
            <Phone size={15} /> Order Now
          </a>
        </nav>

        <button
          data-testid="nav-mobile-toggle"
          onClick={() => setOpen((v) => !v)}
          className="md:hidden text-[#7a1f1f]"
          aria-label="Menu"
        >
          {open ? <X size={26} /> : <MenuIcon size={26} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="md:hidden overflow-hidden bg-[#f7f0e0] border-t border-[#7a1f1f]/10"
          >
            <div className="px-5 py-4 flex flex-col gap-1">
              {LINKS.map((l) => (
                <button
                  key={l.id}
                  data-testid={`nav-mobile-${l.id}`}
                  onClick={() => go(l.id)}
                  className="py-3 text-left font-medium text-[#331818] border-b border-[#7a1f1f]/10"
                >
                  {l.label}
                </button>
              ))}
              <a
                href={`tel:${BRAND.phone.replace(/\s/g, "")}`}
                className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-[#7a1f1f] px-5 py-3 font-semibold text-[#f7f0e0]"
              >
                <Phone size={16} /> {BRAND.phone}
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
